export default function buildMakeValveExerciseSettings({ validate, validators }) {
  return function makeValveExerciseSettings({
    valveId,
    companyId,
    torqueSetPoint,
    rpmSetPoint,
    openDirection,
    currentState,
    revolutions,
    stuckRevs,
    stuckAttempts,
    openRecess,
    closeRecess,
    flushRevolutions,
    flushCycles,
    hardstopSpeed,
    autoCycles,
    exerciseFrequencyMonths,
    torqueSetPointLocked,
    rpmSetPointLocked,
    openDirectionLocked,
    currentStateLocked,
    revolutionsLocked,
    stuckRevsLocked,
    stuckAttemptsLocked,
    openRecessLocked,
    closeRecessLocked,
    flushRevolutionsLocked,
    flushCyclesLocked,
    hardstopSpeedLocked,
    autoCyclesLocked,
    exerciseFrequencyMonthsLocked,
    createdBy,
    createdDate = new Date(),
    modifiedBy,
    modifiedDate = new Date(),
    deletedBy,
    deletedDate,
  }) {
    const schema = {
      valveId: {
        prettyName: 'Valve ID',
        validators: [validators.isString(), validators.exactLength(36)],
      },
      torqueSetPoint: {
        prettyName: 'Torque Set Point',
        validators: [validators.isInteger(), validators.greaterThanOrEqualTo(0), validators.lessThanOrEqualTo(800)],
      },
      rpmSetPoint: {
        prettyName: 'RPM Set Point',
        validators: [validators.isInteger(), validators.greaterThanOrEqualTo(1), validators.lessThanOrEqualTo(255)],
      },
      openDirection: {
        prettyName: 'Open Direction',
        validators: [validators.within(['Left', 'Right'])],
      },
      currentState: {
        prettyName: 'Current State',
        validators: [validators.within(['Open', 'Closed'])],
      },
      revolutions: {
        prettyName: 'Revolutions',
        validators: [validators.isInteger(), validators.greaterThanOrEqualTo(1), validators.lessThanOrEqualTo(255)],
      },
      stuckRevs: {
        prettyName: 'Stuck Revs',
        validators: [validators.isNumber(), validators.greaterThanOrEqualTo(0), validators.lessThanOrEqualTo(50)],
      },
      stuckAttempts: {
        prettyName: 'Stuck Attempts',
        validators: [validators.isInteger(), validators.greaterThanOrEqualTo(0), validators.lessThanOrEqualTo(50)],
      },
      openRecess: {
        prettyName: 'Open Recess',
        validators: [validators.isNumber(), validators.greaterThanOrEqualTo(0), validators.lessThanOrEqualTo(50)],
      },
      closeRecess: {
        prettyName: 'Close Recess',
        validators: [validators.isNumber(), validators.greaterThanOrEqualTo(0), validators.lessThanOrEqualTo(50)],
      },
      flushRevolutions: {
        prettyName: 'Flush Revolutions',
        validators: [validators.isNumber(), validators.greaterThanOrEqualTo(0), validators.lessThanOrEqualTo(50)],
      },
      flushCycles: {
        prettyName: 'Flush Cycles',
        validators: [validators.isInteger(), validators.greaterThanOrEqualTo(0), validators.lessThanOrEqualTo(50)],
      },
      hardstopSpeed: {
        prettyName: 'Hardstop Speed',
        validators: [validators.isInteger(), validators.greaterThanOrEqualTo(0), validators.lessThanOrEqualTo(100)],
      },
      autoCycles: {
        prettyName: 'Auto Cycles',
        validators: [validators.isNumber(), validators.greaterThanOrEqualTo(0), validators.lessThanOrEqualTo(50)],
      },
      exerciseFrequencyMonths: {
        prettyName: 'Exercise Frequency Months',
        validators: [validators.isInteger(), validators.greaterThanOrEqualTo(0)],
      },
      torqueSetPointLocked: {
        prettyName: 'torqueSetPointLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      rpmSetPointLocked: {
        prettyName: 'rpmSetPointLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      openDirectionLocked: {
        prettyName: 'openDirectionLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      currentStateLocked: {
        prettyName: 'currentStateLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      revolutionsLocked: {
        prettyName: 'revolutionsLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      stuckRevsLocked: {
        prettyName: 'stuckRevsLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      stuckAttemptsLocked: {
        prettyName: 'stuckAttemptsLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      openRecessLocked: {
        prettyName: 'openRecessLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      closeRecessLocked: {
        prettyName: 'closeRecessLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      flushRevolutionsLocked: {
        prettyName: 'flushRevolutionsLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      flushCyclesLocked: {
        prettyName: 'flushCyclesLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      hardstopSpeedLocked: {
        prettyName: 'hardstopSpeedLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      autoCyclesLocked: {
        prettyName: 'autoCyclesLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
      exerciseFrequencyMonthsLocked: {
        prettyName: 'exerciseFrequencyMonthsLocked',
        validators: [validators.isBoolean(), validators.notNull()],
      },
    };

    return Object.freeze({
      get valveId() { return valveId },
      get companyId() { return companyId },
      get torqueSetPoint() { return torqueSetPoint },
      get rpmSetPoint() { return rpmSetPoint },
      get openDirection() { return openDirection },
      get currentState() { return currentState },
      get revolutions() { return revolutions },
      get stuckRevs() { return stuckRevs },
      get stuckAttempts() { return stuckAttempts },
      get openRecess() { return openRecess },
      get closeRecess() { return closeRecess },
      get flushRevolutions() { return flushRevolutions },
      get flushCycles() { return flushCycles },
      get hardstopSpeed() { return hardstopSpeed },
      get autoCycles() { return autoCycles },
      get exerciseFrequencyMonths() { return exerciseFrequencyMonths },
      get torqueSetPointLocked() { return torqueSetPointLocked },
      get rpmSetPointLocked() { return rpmSetPointLocked },
      get openDirectionLocked() { return openDirectionLocked },
      get currentStateLocked() { return currentStateLocked },
      get revolutionsLocked() { return revolutionsLocked },
      get stuckRevsLocked() { return stuckRevsLocked },
      get stuckAttemptsLocked() { return stuckAttemptsLocked },
      get openRecessLocked() { return openRecessLocked },
      get closeRecessLocked() { return closeRecessLocked },
      get flushRevolutionsLocked() { return flushRevolutionsLocked },
      get flushCyclesLocked() { return flushCyclesLocked },
      get hardstopSpeedLocked() { return hardstopSpeedLocked },
      get autoCyclesLocked() { return autoCyclesLocked },
      get exerciseFrequencyMonthsLocked() { return exerciseFrequencyMonthsLocked },
      get createdBy() { return createdBy },
      get createdDate() { return createdDate ? new Date(createdDate) : null },
      get modifiedBy() { return modifiedBy },
      get modifiedDate() { return modifiedDate ? new Date(modifiedDate) : null },
      get deletedBy() { return deletedBy },
      get deletedDate() { return deletedDate ? new Date(deletedDate) : null },
      unlock() {
        return makeValveExerciseSettings({
          ...this,
          torqueSetPointLocked: false,
          rpmSetPointLocked: false,
          openDirectionLocked: false,
          currentStateLocked: false,
          revolutionsLocked: false,
          stuckRevsLocked: false,
          stuckAttemptsLocked: false,
          openRecessLocked: false,
          closeRecessLocked: false,
          flushRevolutionsLocked: false,
          flushCyclesLocked: false,
          hardstopSpeedLocked: false,
          autoCyclesLocked: false,
          exerciseFrequencyMonthsLocked: false,
        });
      },
      lock() {
        return makeValveExerciseSettings({
          ...this,
          torqueSetPointLocked: true,
          rpmSetPointLocked: true,
          openDirectionLocked: true,
          currentStateLocked: true,
          revolutionsLocked: true,
          stuckRevsLocked: true,
          stuckAttemptsLocked: true,
          openRecessLocked: true,
          closeRecessLocked: true,
          flushRevolutionsLocked: true,
          flushCyclesLocked: true,
          hardstopSpeedLocked: true,
          autoCyclesLocked: true,
          exerciseFrequencyMonthsLocked: true,
        });
      },
      validate() {
        const errors = validate(this, schema);
        if (errors.length > 0) {
          const message = errors.map(e => e.name + ': ' + e.message).join('\n');
          const validationError = new Error(message);
          validationError.name = 'ValidationError';
          throw validationError;
        }
      }
    });
  }
}